import AppVal from 'constants/frontend/app/AppVal';
import UserLocalStorage, { UserThemeModeT } from 'utils/UserLocalStorage';

export type ThemeMode = UserThemeModeT;

type ThemeListener = (theme: ThemeMode) => void;

class ThemeManager {
  private static readonly neutral = {
    100: '#F3F4F6',
    200: '#dee0e3',
    300: '#c2c4c7',
    400: '#a1a3a9',
    500: '#7e8188',
    600: '#5e6168',
    700: '#42444d',
    800: '#2b2d37',
    900: '#1C1F28',
  };
  private static readonly neutralOrder = [
    100, 200, 300, 400, 500, 600, 700, 800, 900,
  ] as const;

  private static readonly listeners = new Set<ThemeListener>();
  private static theme: ThemeMode = UserLocalStorage.getItem(
    AppVal.ULK_PREFERENCE_THEME,
  );

  static getTheme(): ThemeMode {
    return ThemeManager.theme;
  }

  static setTheme(theme: ThemeMode): void {
    ThemeManager.theme = theme;
    UserLocalStorage.setItem(AppVal.ULK_PREFERENCE_THEME, theme);
    ThemeManager.listeners.forEach((listener) => listener(theme));
  }

  static loadCurrentUserTheme(): void {
    ThemeManager.theme = UserLocalStorage.getItem(
      AppVal.ULK_PREFERENCE_THEME,
    );
    ThemeManager.listeners.forEach((listener) =>
      listener(ThemeManager.theme),
    );
  }

  static getNeutral(level: number): string {
    const safeLevel = Number.isFinite(level) ? level : 5;
    const normalizedLevel = Math.min(9, Math.max(1, Math.round(safeLevel)));
    const index = normalizedLevel - 1;
    const neutralIndex =
      ThemeManager.theme === 'dark'
        ? ThemeManager.neutralOrder.length - 1 - index
        : index;
    const key = ThemeManager.neutralOrder[neutralIndex];

    return ThemeManager.neutral[key];
  }

  static subscribe(listener: ThemeListener): () => void {
    ThemeManager.listeners.add(listener);
    return () => ThemeManager.listeners.delete(listener);
  }
}

export default ThemeManager;
